import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/page-header";
import { Input } from "@/components/ui/input";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useList } from "@/hooks/use-list";
import { api } from "@/lib/api";
import { Plus, Trash2 } from "lucide-react";

export default function GovOpportunitiesPage() {
  const [q, setQ] = useState("");
  const qc = useQueryClient();
  const { data, isLoading } = useList<any>("/gov-opportunities", { q, pageSize: 50 });

  async function del(id: string, title: string) {
    if (!confirm(`Delete opportunity "${title}"?`)) return;
    await api.delete(`/gov-opportunities/${id}`);
    qc.invalidateQueries({ queryKey: ["/gov-opportunities"] });
  }

  return (
    <>
      <PageHeader
        title="Government Opportunities"
        description="SAM.gov pull + manual entries. Bid/no-bid, capture, proposal."
        actions={<Button asChild><Link to="/gov-opportunities/new"><Plus className="mr-1 h-4 w-4"/>New Opportunity</Link></Button>}
      />
      <div className="p-6 space-y-4">
        <Input placeholder="Search by title, agency, or solicitation…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-md" />
        <div className="rounded-md border bg-card">
          <Table>
            <Thead>
              <Tr>
                <Th>Solicitation</Th><Th>Title</Th><Th>Agency</Th><Th>NAICS</Th><Th>Due</Th><Th>Stage</Th><Th className="w-10"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {isLoading && <Tr><Td colSpan={7} className="py-8 text-center text-muted-foreground">Loading…</Td></Tr>}
              {!isLoading && (data?.data?.length ?? 0) === 0 && (
                <Tr><Td colSpan={7} className="py-8 text-center text-muted-foreground">No opportunities yet. Add SAM.gov API key to auto-populate.</Td></Tr>
              )}
              {data?.data?.map((o: any) => (
                <Tr key={o.id}>
                  <Td className="text-xs font-mono">{o.solicitationNumber}</Td>
                  <Td className="font-medium"><Link to={`/gov-opportunities/${o.id}`} className="hover:underline">{o.title}</Link></Td>
                  <Td className="text-muted-foreground">{o.agency}</Td>
                  <Td>{o.naicsCode ?? "—"}</Td>
                  <Td>{o.responseDueDate ? new Date(o.responseDueDate).toLocaleDateString() : "—"}</Td>
                  <Td><Badge variant="secondary">{o.stage}</Badge></Td>
                  <Td>
                    <Button variant="ghost" size="icon" onClick={() => del(o.id, o.title)} aria-label="Delete">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
      </div>
    </>
  );
}
